import React from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { 
  ArrowRight, 
  Check, 
  X, 
  Rocket,
  TrendingUp,
  Download
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { useGettingStartedData } from "../getting-started-guide/shared/useGettingStartedData";
import { calculateGettingStartedProgress, getStepsWithStatus } from "../getting-started-guide/shared/gettingStartedUtils";
import { GettingStartedStep } from "../getting-started-guide/shared/gettingStartedSteps";

const LOCAL_STORAGE_KEY = "wealthbee-getting-started-dismissed";

interface GettingStartedGuideProps {
  onDismiss?: () => void;
}

/**
 * A getting started guide component displayed on the dashboard
 * Provides a step-by-step guide to help users get started with the platform
 * Can be dismissed by the user and uses localStorage to remember this preference
 */
export const GettingStartedGuide = ({
  onDismiss,
}: GettingStartedGuideProps) => {
  const navigate = useNavigate();
  const { data, isLoading } = useGettingStartedData();
  const progress = calculateGettingStartedProgress(data);
  const stepsWithStatus = getStepsWithStatus(data);

  const handleDismiss = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    if (onDismiss) {
      onDismiss();
    }
  };

  const StepCard = ({ step, index }: { step: any; index: number }) => {
    const Icon = step.icon;
    const isNext = progress.nextStep?.id === step.id;
    
    return (
      <div 
        className={`relative bg-card border rounded-xl p-6 transition-all duration-300 hover:shadow-md ${
          step.completed 
            ? 'border-green-200 bg-green-50/50 dark:bg-green-950/20 dark:border-green-800' 
            : isNext 
              ? 'border-primary/50 bg-primary/5 ring-2 ring-primary/20' 
              : step.enabled 
                ? 'border-border hover:border-primary/30' 
                : 'border-muted bg-muted/30 opacity-60'
        }`}
      >
        {/* Step indicator */}
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 h-12 w-12 rounded-xl flex items-center justify-center font-medium transition-all ${
            step.completed 
              ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' 
              : isNext 
                ? 'bg-primary text-primary-foreground shadow-md' 
                : step.enabled 
                  ? 'bg-primary/10 text-primary' 
                  : 'bg-muted text-muted-foreground'
          }`}>
            {step.completed ? <Check className="h-6 w-6" /> : <Icon className="h-6 w-6" />}
          </div>
          
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className={`font-semibold text-base ${
                step.completed ? 'text-green-700 dark:text-green-300' : 'text-foreground'
              }`}>
                {step.title}
              </h4>
              <div className="flex items-center gap-2">
                {step.estimatedTime && (
                  <span className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full">
                    {step.estimatedTime}
                  </span>
                )}
                {step.completed && (
                  <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 px-2 py-1 rounded-full font-medium">
                    Complete
                  </span>
                )}
                {isNext && (
                  <span className="text-xs bg-primary text-primary-foreground px-2 py-1 rounded-full font-medium animate-pulse">
                    Next
                  </span>
                )}
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground leading-relaxed">
              {step.description}
            </p>
            
            <p className="text-xs text-muted-foreground/80">
              {step.detail}
            </p>
            
            {/* Mock download links */}
            {step.mockDownloads && step.mockDownloads.length > 0 && (
              <div className="mt-3 space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Sample data templates:</p>
                <div className="flex flex-wrap gap-2">
                  {step.mockDownloads.map((download: any, idx: number) => (
                    <Button
                      key={idx}
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs bg-muted/50 hover:bg-muted"
                      onClick={() => {
                        // Create a temporary link to download the file
                        const link = document.createElement('a');
                        link.href = download.url;
                        link.download = download.filename;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }}
                    >
                      <Download className="mr-1 h-3 w-3" />
                      {download.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            
            {step.enabled && !step.completed && !isLoading && (
              <Button
                variant={isNext ? "default" : "outline"}
                size="sm"
                className={`mt-3 ${isNext ? 'shadow-md hover:shadow-lg' : ''}`}
                onClick={() => navigate(step.route)}
              >
                {isNext ? 'Get Started' : 'Continue'} 
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* Progress connector */}
        {index < stepsWithStatus.length - 1 && (
          <div className="absolute left-6 top-20 w-0.5 h-8 bg-border"></div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 my-12 relative">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-background border rounded-2xl p-8 mb-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/5 rounded-full translate-y-12 -translate-x-12"></div>
          
          <div className="relative z-10">
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center">
                  <Rocket className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Getting Started</h2>
                  <p className="text-muted-foreground">Complete these steps to set up your fund management platform</p>
                </div>
              </div>
              {/* <Button
                variant="ghost"
                size="icon"
                onClick={handleDismiss}
                className="h-10 w-10 rounded-full hover:bg-background/50"
              >
                <X className="h-5 w-5" />
              </Button> */}
            </div>
            
            {/* Progress section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">Progress</span>
                <span className="text-muted-foreground">
                  {progress.completedCount} of {progress.totalCount} steps completed
                </span>
              </div>
              <Progress value={progress.progressPercentage} className="h-3" />
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {progress.isComplete ? (
                    <span className="flex items-center gap-2 text-green-600 dark:text-green-400 font-medium">
                      <TrendingUp className="h-4 w-4" />
                      Setup Complete!
                    </span>
                  ) : progress.nextStep ? (
                    `Next: ${progress.nextStep.title}`
                  ) : (
                    'Continue your setup'
                  )}
                </span>
                <span className="text-muted-foreground">
                  {Math.round(progress.progressPercentage)}% complete
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="space-y-4">
          {stepsWithStatus.map((step, index) => (
            <StepCard key={step.id} step={step} index={index} />
          ))}
        </div>

        {/* Footer message */}
        {progress.isComplete && (
          <div className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border border-green-200 dark:border-green-800 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 flex items-center justify-center">
                <Check className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-green-800 dark:text-green-200">Congratulations!</h3>
                <p className="text-sm text-green-600 dark:text-green-300">
                  You've completed the initial setup. Your fund management platform is ready to use.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 